/**
 * kisiKunyeService.js — Kişi Detay sayfasının künye başlığı ve "Düzenle ve Gör" penceresi (backlog #367 / #366 / #351).
 *
 * Staff'a özel üç uç:
 *   · GET   /personnel/{personId}/kunye      — künye + kimlik doğrulama + değişiklik geçmişi (ZARFSIZ: nesne doğrudan)
 *   · PATCH /personnel/{personId}/kunye      — YALNIZCA gönderilen alanları yazar (zarflı: { message, result, data })
 *   · POST  /personnel/{personId}/fotograf   — kişinin vesikalığını koyar / değiştirir (multipart, alan adı `file`)
 *
 * ⚠️ `PUT /personnel/kartduzenle/{id}` BU İŞ İÇİN KULLANILMAZ: tüm satırı yeniden yazar, gövdede olmayan alanı siler.
 * ⚠️ Fotoğraf (person_logo) ≠ kimlik belgesi (file_type 90); ikisi ayrı uçlardır (kisiAdminService.uploadKimlikBelgesi).
 *
 * Hata sözleşmesi (rehber FE-PersonnelController-guide.md §18): fırlatılan `Error`da `.status`, `.data` (zarfın data'sı),
 * `.kod` ve duruma göre:
 *   · 400 → `.hatalar`  ({ alan: mesaj }, her mesaj kendi alanının altına)
 *   · 409 KIMLIK_NO_KULLANIMDA  → `.personId`, `.kartlar[]` (başka kartta aynı numara; birleştirme #289 gerekir)
 *   · 409 BENZER_KAYIT          → `.adaylar[]` (aynı ad soyad, numara tek hane farklı; `benzerKayitOnaylandi: true` ile tekrar)
 *   · 409 BIRLESTIRILMIS_KART   → `.kalanPersonId` (düzenlenecek güncel kart)
 *   · 403 KIMLIK_DOGRULANMIS    → yalnızca firma girişi için (fotoğraf); staff her zaman değiştirebilir
 */
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import { getUlkeler } from './portalService';

const BASE = `${JAVA_BACKEND_API_URL}/personnel`;

function parseError(error) {
  const govde = error.response?.data;
  const veri = govde && typeof govde === 'object' ? govde.data ?? null : null;
  const message =
    (typeof govde === 'string' && govde) || govde?.message || error.message || 'Bilinmeyen hata';
  const hata = new Error(message);
  hata.status = error.response?.status ?? null;
  hata.data = veri;
  hata.kod = veri?.kod ?? null;
  if (hata.status === 400) hata.hatalar = veri?.hatalar && typeof veri.hatalar === 'object' ? veri.hatalar : null;
  if (hata.status === 409) {
    if (hata.kod === 'KIMLIK_NO_KULLANIMDA') {
      hata.personId = veri.personId ?? null;
      hata.kartlar = Array.isArray(veri.kartlar) ? veri.kartlar : [];
    } else if (hata.kod === 'BENZER_KAYIT') {
      // createPersonAdmin (personnelAdminService.js) ile aynı kalıp: dallanma mesaja değil `kod`a göre.
      hata.adaylar = Array.isArray(veri.adaylar) ? veri.adaylar : [];
    } else if (hata.kod === 'BIRLESTIRILMIS_KART') {
      hata.kalanPersonId = veri.kalanPersonId ?? null;
    }
  }
  throw hata;
}

/** `{ message, result, data }` zarfını açar; `result:false` HTTP 200 ile gelebilir → hataya çevrilir. */
function zarfiAc(response) {
  const zarf = response.data;
  if (zarf && typeof zarf === 'object' && zarf.result === false) {
    const hata = new Error(zarf.message || 'İşlem başarısız oldu.');
    hata.status = response.status;
    hata.data = zarf.data ?? null;
    hata.kod = zarf.data?.kod ?? null;
    throw hata;
  }
  return { ...(zarf?.data && typeof zarf.data === 'object' ? zarf.data : {}), message: zarf?.message ?? null };
}

/**
 * Kişinin künyesi: { personId, kimlikNo, ad, soyad, uyrukId, uyrukAdi, telefon, eposta, pasaportNo,
 * pasaportUlkeId, guncellemeTarihi, birlestirildigiPersonId, kimlikDogrulama{}, gecmis[] }. Birleştirilmiş kart da
 * okunur (`birlestirildigiPersonId` dolu → yalnızca okuma). Zarf YOK; yine de zarflı gelirse `data` açılır.
 * GET /api/v1/personnel/{personId}/kunye
 */
export async function getKunye(personId) {
  try {
    const response = await axios.get(`${BASE}/${personId}/kunye`, { headers: getAuthHeader() });
    const govde = response.data;
    return govde && typeof govde === 'object' && 'result' in govde && 'data' in govde ? govde.data : govde;
  } catch (error) {
    parseError(error);
  }
}

/**
 * Künyenin yalnızca gönderilen alanlarını yazar. Anahtarlar: kimlikNo, ad, soyad, uyrukId (1 T.C. / 2 Yabancı),
 * telefon, eposta, pasaportNo, pasaportUlkeId (+ `benzerKayitOnaylandi`). Eksik anahtar = dokunulmaz;
 * telefon / eposta / pasaportNo / pasaportUlkeId için `null` temizler; ilk dört kimlik alanı temizlenemez.
 * Bilinmeyen anahtar ya da boş gövde 400.
 *
 * Başarıda { ...künye, degisenAlanlar[], kimlikDogrulamaSifirlandi, message } döner. Hiçbir şey farklı değilse
 * "Değişiklik yok." + `degisenAlanlar: []`. Yeni T.C. / uyruk (ya da pasaportla doğrulanmış kartta yeni pasaport no)
 * kimlik doğrulamasını Doğrulanmadı'ya sıfırlar → `kimlikDogrulamaSifirlandi: true`.
 * PATCH /api/v1/personnel/{personId}/kunye
 */
export async function patchKunye(personId, body) {
  let response;
  try {
    response = await axios.patch(`${BASE}/${personId}/kunye`, body, { headers: getAuthHeader() });
  } catch (error) {
    parseError(error);
  }
  return zarfiAc(response);
}

/**
 * Kişinin vesikalığını koyar / değiştirir (kimlik belgesi DEĞİL). Multipart, alan adı `file`; uzantı + içerik
 * (PNG / JPEG / WEBP) ve boyut sunucuda (Dosya Ayarları › Kişi fotoğrafı) denetlenir → 400. Eski dosya diskte kalır.
 * Başarıda { personId, logo, fotografYukleme, message }.
 * POST /api/v1/personnel/{personId}/fotograf
 */
export async function uploadKisiFotograf(personId, file) {
  const form = new FormData();
  form.append('file', file);
  let response;
  try {
    response = await axios.post(`${BASE}/${personId}/fotograf`, form, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
    });
  } catch (error) {
    parseError(error);
  }
  return zarfiAc(response);
}

// Pasaport ülkesi listesi: KimlikPasaportModal ile aynı kaynak (GET /portal/ulkeler → [{countryId, countryName}]).
// Başlık ve pencere aynı listeyi okuduğu için oturum boyunca bir kez çekilir; hata olursa önbellek tutulmaz.
let ulkeOnbellek = null;

export function getUlkeListesi() {
  if (!ulkeOnbellek) {
    ulkeOnbellek = Promise.resolve(getUlkeler())
      .then((liste) => (Array.isArray(liste) ? liste : []))
      .catch((error) => {
        ulkeOnbellek = null;
        throw error;
      });
  }
  return ulkeOnbellek;
}
